import React, { useState, useCallback, createContext, useContext, useEffect } from 'react';
import CustomAlert from '../components/common/custom-alert';
import { Alert } from '../utils/alert';
import { log } from '../services/production-logger';


interface AlertButton {
  text: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
}

interface AlertOptions {
  title: string;
  message?: string;
  buttons?: AlertButton[];
  type?: 'info' | 'success' | 'warning' | 'error';
  dismissable?: boolean;
}

interface AlertContextType {
  showAlert: (options: AlertOptions) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alertConfig, setAlertConfig] = useState<AlertOptions | null>(null);
  const [visible, setVisible] = useState(false);

  const showAlert = useCallback((options: AlertOptions) => {
    setAlertConfig(options);
    setVisible(true);
  }, []);

  const hideAlert = useCallback(() => {
    setVisible(false);
    setTimeout(() => setAlertConfig(null), 300); // Wait for animation
  }, []);

  // Set up the Alert utility to use our custom alert
  useEffect(() => {
    Alert.setShowAlert((title, message, buttons, options) => {
      showAlert({
        title,
        message,
        buttons,
        type: options?.type,
        dismissable: options?.cancelable,
      });
    });
  }, [showAlert]);

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      {alertConfig && (
        <CustomAlert
          visible={visible}
          title={alertConfig.title}
          message={alertConfig.message}
          buttons={alertConfig.buttons}
          type={alertConfig.type}
          dismissable={alertConfig.dismissable}
          onDismiss={hideAlert}
        />
      )}
    </AlertContext.Provider>
  );
};

export const useCustomAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useCustomAlert must be used within an AlertProvider');
  }
  return context;
};

// Convenience function that mimics Alert.alert API
export const showCustomAlert = (
  title: string,
  message?: string,
  buttons?: AlertButton[],
  type?: 'info' | 'success' | 'warning' | 'error',
) => {
  // This will be used as a fallback when the hook isn't available
  log.warn('Custom alert called outside of AlertProvider context');
};
